import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Download, FileText, Play, Printer, ShieldCheck, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ReportPeriodSelector, { getMonthDateRange, getYearDateRange } from './ReportPeriodSelector';
import ReportWorkflowTrail from './ReportWorkflowTrail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const downloadCsv = (filename, rows) => {
    if (!rows?.length) {
        toast.error('No rows to export');
        return;
    }
    const headers = Object.keys(rows[0]);
    const csv = [
        headers.join(','),
        ...rows.map(row => headers.map(header => csvCell(row[header])).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename || 'ap_withholding.csv';
    link.click();
    URL.revokeObjectURL(link.href);
};

export default function APWithholdingReport({ suppliers = [], fmt, viewingStudentId, viewingStudentName, onNavigate, onOpenSource }) {
    const now = new Date();
    const initialMonth = now.getMonth() + 1;
    const initialYear = now.getFullYear();
    const initialRange = getMonthDateRange(initialYear, initialMonth);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [periodMode, setPeriodMode] = useState('monthly');
    const [periodMonth, setPeriodMonth] = useState(initialMonth);
    const [periodYear, setPeriodYear] = useState(initialYear);
    const [filters, setFilters] = useState({
        date_from: initialRange.dateFrom,
        date_to: initialRange.dateTo,
        date_type: 'transaction',
        supplier: 'all',
        status: 'posted'
    });

    const applyPeriodMode = (mode) => {
        setPeriodMode(mode);
        setFilters(prev => {
            if (mode === 'monthly') {
                const range = getMonthDateRange(periodYear, periodMonth);
                return { ...prev, date_from: range.dateFrom, date_to: range.dateTo };
            }
            if (mode === 'annual') {
                const range = getYearDateRange(periodYear);
                return { ...prev, date_from: range.dateFrom, date_to: range.dateTo };
            }
            return {
                ...prev,
                date_from: prev.date_from || initialRange.dateFrom,
                date_to: prev.date_to || initialRange.dateTo,
            };
        });
    };

    const handlePeriodMonthChange = (month) => {
        setPeriodMode('monthly');
        setPeriodMonth(month);
        const range = getMonthDateRange(periodYear, month);
        setFilters(prev => ({ ...prev, date_from: range.dateFrom, date_to: range.dateTo }));
    };

    const handlePeriodYearChange = (year) => {
        setPeriodYear(year);
        const range = periodMode === 'annual'
            ? getYearDateRange(year)
            : getMonthDateRange(year, periodMonth);
        setFilters(prev => ({ ...prev, date_from: range.dateFrom, date_to: range.dateTo }));
    };

    const handleCustomDateChange = (field, value) => {
        setPeriodMode('custom');
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const openApWtaxTrail = (tab, label) => {
        onNavigate?.(tab);
        toast.info(`Opening ${label}. Use supplier TIN, ATC, voucher number, and posting status to trace this AP WTAX evidence back to source work.`);
    };

    const openVoucherSource = (row) => {
        const voucherNumber = row.voucher_number || '';
        onOpenSource?.({
            source_type: 'ap_voucher',
            workspace_tab: 'apvouchers',
            record_id: row.voucher_id || '',
            record_number: voucherNumber,
            doc_number: voucherNumber,
            record_type: 'AP Voucher',
            source_location: 'AP WTAX voucher row',
            source_impact: 'AP WTAX preview reads voucher withholding fields; posted vouchers also affect GL and Trial Balance.',
            source_next_action: 'Review supplier TIN, ATC, tax basis, WTAX amount, posting status, and GL evidence before 2307/correction work.',
        });
    };

    const supplierLabel = (supplier) => {
        const id = supplier.supplier_id || supplier.id || '';
        const name = supplier.supplier_name || supplier.name || 'Supplier';
        return id ? `${id} - ${name}` : name;
    };

    const handleGenerate = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            params.append('date_type', filters.date_type);
            params.append('date_from', filters.date_from);
            params.append('date_to', filters.date_to);
            params.append('status', filters.status);
            if (filters.supplier && filters.supplier !== 'all') {
                params.append('supplier', filters.supplier);
            }
            if (viewingStudentId) {
                params.append('student_id', viewingStudentId);
            }

            const response = await axios.get(`${API}/reports/ap-withholding?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(response.data);
            toast.success('AP WTAX report generated on this report tab. It uses captured AP withholding evidence for the selected filters; no vouchers, payments, GL, or Trial Balance records were changed.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate AP WTAX report');
        } finally {
            setLoading(false);
        }
    }, [filters, viewingStudentId]);

    const handleExport = useCallback(() => {
        if (!data?.export?.data?.length) {
            toast.error('No withholding rows to export');
            return;
        }
        downloadCsv(data.export.filename, data.export.data);
        toast.success(`Exported ${data.export.data.length} AP WTAX rows. Export only; AP source records and GL/Trial Balance balances were not changed.`);
    }, [data]);

    return (
        <div className="space-y-6" data-testid="ap-withholding-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">AP WTAX Report</h2>
                    <p className="text-sm text-slate-500">
                        {viewingStudentName ? `Expanded withholding tax evidence for ${viewingStudentName}` : 'Expanded withholding tax evidence from AP vouchers'}
                    </p>
                </div>
            </div>
            {viewingStudentName && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="ap-wtax-student-scope">
                    Student review scope is active. AP WTAX evidence is calculated only from {viewingStudentName}'s AP vouchers.
                </div>
            )}
            <ReportWorkflowTrail
                title="AP WTAX trail"
                testId="ap-wtax-workflow-trail"
                scope={viewingStudentName ? `Reviewing ${viewingStudentName}` : 'Your workspace'}
                items={[
                    {
                        label: 'Source records',
                        value: 'Uses AP vouchers with captured withholding tax evidence inside the selected date coverage.'
                    },
                    {
                        label: 'Where to verify',
                        value: 'Open AP Vouchers to inspect supplier TIN, ATC, tax basis, WTAX amount, and voucher posting state.'
                    },
                    {
                        label: 'GL / Trial Balance',
                        value: filters.status === 'posted'
                            ? 'Posted vouchers create withholding liability evidence that affects GL and Trial Balance.'
                            : 'Draft, cancelled, or reversed rows are review evidence only unless they are posted.'
                    },
                    {
                        label: 'Next safe action',
                        value: 'Preview WTAX evidence, review 2307 readiness, then correct the source voucher if supplier tax details are incomplete.'
                    }
                ]}
            />

            <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <Label className="text-sm">Date Type</Label>
                        <Select
                            value={filters.date_type}
                            onValueChange={(value) => setFilters(prev => ({ ...prev, date_type: value }))}
                        >
                            <SelectTrigger className="mt-1" data-testid="ap-wtax-date-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="transaction">Voucher Date</SelectItem>
                                <SelectItem value="posting">Posting Date</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-sm">Supplier</Label>
                        <Select
                            value={filters.supplier}
                            onValueChange={(value) => setFilters(prev => ({ ...prev, supplier: value }))}
                        >
                            <SelectTrigger className="mt-1" data-testid="ap-wtax-supplier">
                                <SelectValue placeholder="All suppliers" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Suppliers</SelectItem>
                                {suppliers.map((supplier) => {
                                    const value = supplier.supplier_id || supplier.id;
                                    return value ? (
                                        <SelectItem key={value} value={value}>
                                            {supplierLabel(supplier)}
                                        </SelectItem>
                                    ) : null;
                                })}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-sm">Status</Label>
                        <Select
                            value={filters.status}
                            onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                        >
                            <SelectTrigger className="mt-1" data-testid="ap-wtax-status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="posted">Posted</SelectItem>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                                <SelectItem value="reversed">Reversed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <ReportPeriodSelector
                    mode={periodMode}
                    onModeChange={applyPeriodMode}
                    month={periodMonth}
                    year={periodYear}
                    onMonthChange={handlePeriodMonthChange}
                    onYearChange={handlePeriodYearChange}
                    dateFrom={filters.date_from}
                    dateTo={filters.date_to}
                    onDateFromChange={(value) => handleCustomDateChange('date_from', value)}
                    onDateToChange={(value) => handleCustomDateChange('date_to', value)}
                    title="AP WTAX Date Coverage"
                    effectText="AP vouchers with withholding tax evidence inside this coverage appear here; only posted vouchers affect GL and Trial Balance."
                    testId="ap-wtax-date-coverage"
                />

                <div className="flex gap-3 pt-2">
                    <Button
                        onClick={handleGenerate}
                        disabled={loading}
                        className="bg-[#2F8FCB] hover:bg-[#2577a8]"
                        data-testid="ap-wtax-preview-btn"
                    >
                        {loading ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <Play className="w-4 h-4 mr-2" /> Preview
                            </>
                        )}
                    </Button>
                    <Button variant="outline" onClick={() => window.print()} disabled={!data}>
                        <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleExport}
                        disabled={!data?.export?.data?.length}
                        data-testid="ap-wtax-export-btn"
                    >
                        <Download className="w-4 h-4 mr-2" /> Export CSV
                    </Button>
                </div>
            </div>

            {!data && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-12 text-center">
                    <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-600 mb-2">Click "Preview" to generate AP WTAX evidence</h3>
                    <p className="text-sm text-slate-400">Use this for reviewing AP vouchers with captured withholding tax.</p>
                </div>
            )}

            {data && (
                <div className="space-y-4">
                    <div className="bg-[#0F2A44] text-white rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                    <ShieldCheck className="w-5 h-5" /> AP WTAX Evidence
                                </h3>
                                <p className="text-white/70 text-sm">
                                    {data.date_from} to {data.date_to} | {data.status_filter === 'all' ? 'All Status' : data.status_filter}
                                </p>
                            </div>
                            <div className="text-right">
                                <p className="text-xs text-white/50">Generated</p>
                                <p className="text-sm">{new Date(data.generated_at).toLocaleString()}</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4" data-testid="ap-wtax-where-it-appears">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-4 flex-1">
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">What changed</p>
                                    <p className="mt-1 text-sm text-slate-700">The WTAX evidence report was generated from AP voucher withholding fields. No voucher, payment, GL, or Trial Balance record was edited.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Where to find it</p>
                                    <p className="mt-1 text-sm text-slate-700">Supplier tax data and voucher WTAX details stay in AP Vouchers. AP Summary shows the payable totals; General Ledger shows posted liability evidence.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Report effect</p>
                                    <p className="mt-1 text-sm text-slate-700">Posted vouchers affect GL and Trial Balance. Draft, cancelled, or reversed rows are evidence only unless posted.</p>
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Next safe action</p>
                                    <p className="mt-1 text-sm text-slate-700">Review supplier TIN, ATC, tax basis, and readiness issues before preparing 2307 evidence or correcting the source voucher.</p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 shrink-0">
                                <Button type="button" variant="outline" size="sm" onClick={() => openApWtaxTrail('apvouchers', 'AP Vouchers')} data-testid="ap-wtax-open-vouchers">
                                    AP Vouchers <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => openApWtaxTrail('apsummary', 'AP Summary')} data-testid="ap-wtax-open-summary">
                                    AP Summary <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={() => openApWtaxTrail('glentries', 'General Ledger')} data-testid="ap-wtax-open-gl">
                                    General Ledger <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="bg-white rounded-xl border border-blue-200 p-4">
                            <p className="text-sm text-slate-500">Gross Amount</p>
                            <p className="text-2xl font-bold text-blue-600">{fmt(data.totals.gross_amount)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-indigo-200 p-4">
                            <p className="text-sm text-slate-500">Tax Basis</p>
                            <p className="text-2xl font-bold text-indigo-600">{fmt(data.totals.tax_basis)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-rose-200 p-4">
                            <p className="text-sm text-slate-500">WTAX Amount</p>
                            <p className="text-2xl font-bold text-rose-600">{fmt(data.totals.withholding_amount)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-emerald-200 p-4">
                            <p className="text-sm text-slate-500">Net Payable</p>
                            <p className="text-2xl font-bold text-emerald-600">{fmt(data.totals.net_payable)}</p>
                        </div>
                        <div className="bg-white rounded-xl border border-slate-200 p-4">
                            <p className="text-sm text-slate-500">Vouchers</p>
                            <p className="text-2xl font-bold text-slate-800">{data.totals.voucher_count}</p>
                            <p className="text-xs text-slate-400">{data.totals.supplier_count} suppliers</p>
                        </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-lg overflow-auto">
                        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#2F8FCB]" />
                            <h4 className="font-semibold text-slate-900">Summary by ATC</h4>
                        </div>
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                <tr>
                                    <th className="text-left px-4 py-3">ATC</th>
                                    <th className="text-center px-4 py-3">Vouchers</th>
                                    <th className="text-right px-4 py-3">Tax Basis</th>
                                    <th className="text-right px-4 py-3">WTAX</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {data.summary_by_atc.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="px-4 py-6 text-center text-slate-400">No withholding data found</td>
                                    </tr>
                                ) : (
                                    data.summary_by_atc.map((row) => (
                                        <tr key={row.withholding_atc} className="hover:bg-slate-50">
                                            <td className="px-4 py-3 font-mono">{row.withholding_atc}</td>
                                            <td className="px-4 py-3 text-center">{row.voucher_count}</td>
                                            <td className="px-4 py-3 text-right font-mono">{fmt(row.tax_basis)}</td>
                                            <td className="px-4 py-3 text-right font-mono text-rose-600">{fmt(row.withholding_amount)}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-lg overflow-auto">
                        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                                <h4 className="font-semibold text-slate-900">2307 Evidence by Supplier</h4>
                            </div>
                            <span className="text-xs text-slate-500">
                                {data.totals.certificate_ready_count || 0} ready / {data.totals.certificate_candidate_count || 0} candidates
                            </span>
                        </div>
                        <table className="w-full min-w-[980px] text-sm">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                <tr>
                                    <th className="text-left px-4 py-3">Supplier</th>
                                    <th className="text-left px-4 py-3">Period</th>
                                    <th className="text-left px-4 py-3">ATC</th>
                                    <th className="text-right px-4 py-3">Tax Basis</th>
                                    <th className="text-right px-4 py-3">WTAX</th>
                                    <th className="text-center px-4 py-3">Vouchers</th>
                                    <th className="text-left px-4 py-3">Readiness</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {!data.certificate_candidates?.length ? (
                                    <tr>
                                        <td colSpan="7" className="px-4 py-6 text-center text-slate-400">No 2307 evidence candidates found</td>
                                    </tr>
                                ) : (
                                    data.certificate_candidates.map((candidate) => (
                                        <tr
                                            key={`${candidate.supplier_id}-${candidate.withholding_atc}-${candidate.year}-Q${candidate.quarter}`}
                                            className="hover:bg-slate-50"
                                        >
                                            <td className="px-4 py-3">
                                                <p className="font-medium">{candidate.supplier_name || '-'}</p>
                                                <p className="text-xs text-slate-400">
                                                    {candidate.supplier_tin ? `TIN ${candidate.supplier_tin}` : 'TIN missing'}
                                                </p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <p className="font-medium">Q{candidate.quarter} {candidate.year}</p>
                                                <p className="text-xs text-slate-400">{candidate.period_from} to {candidate.period_to}</p>
                                            </td>
                                            <td className="px-4 py-3 font-mono">{candidate.withholding_atc || '-'}</td>
                                            <td className="px-4 py-3 text-right font-mono">{fmt(candidate.tax_basis)}</td>
                                            <td className="px-4 py-3 text-right font-mono text-rose-600">{fmt(candidate.withholding_amount)}</td>
                                            <td className="px-4 py-3 text-center">{candidate.voucher_count}</td>
                                            <td className="px-4 py-3">
                                                {candidate.ready_for_2307 ? (
                                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                                                        Ready
                                                    </span>
                                                ) : (
                                                    <div className="space-y-1">
                                                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                                            Review
                                                        </span>
                                                        <p className="text-xs text-slate-500">{candidate.readiness_issues.join(', ')}</p>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-lg overflow-auto">
                        <table className="w-full min-w-[1100px] text-sm">
                            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                                <tr>
                                    <th className="text-left px-4 py-3">Voucher</th>
                                    <th className="text-left px-4 py-3">Date</th>
                                    <th className="text-left px-4 py-3">Supplier</th>
                                    <th className="text-left px-4 py-3">TIN</th>
                                    <th className="text-left px-4 py-3">ATC</th>
                                    <th className="text-right px-4 py-3">Tax Basis</th>
                                    <th className="text-right px-4 py-3">Rate</th>
                                    <th className="text-right px-4 py-3">WTAX</th>
                                    <th className="text-right px-4 py-3">Gross</th>
                                    <th className="text-right px-4 py-3">Net Payable</th>
                                    <th className="text-center px-4 py-3">Status</th>
                                    <th className="text-center px-4 py-3">Source</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {data.rows.length === 0 ? (
                                    <tr>
                                        <td colSpan="12" className="px-4 py-8 text-center text-slate-400">No AP vouchers with WTAX found</td>
                                    </tr>
                                ) : (
                                    data.rows.map((row) => (
                                        <tr key={row.voucher_id || row.voucher_number} className="hover:bg-slate-50">
                                            <td className="px-4 py-3 font-mono">{row.voucher_number}</td>
                                            <td className="px-4 py-3">{row.voucher_date}</td>
                                            <td className="px-4 py-3">
                                                <p className="font-medium">{row.supplier_name}</p>
                                                <p className="text-xs text-slate-400">{row.supplier_id}</p>
                                            </td>
                                            <td className="px-4 py-3 font-mono">{row.supplier_tin || '-'}</td>
                                            <td className="px-4 py-3 font-mono">{row.withholding_atc || '-'}</td>
                                            <td className="px-4 py-3 text-right font-mono">{fmt(row.tax_basis)}</td>
                                            <td className="px-4 py-3 text-right font-mono">{row.withholding_rate}%</td>
                                            <td className="px-4 py-3 text-right font-mono text-rose-600">{fmt(row.withholding_amount)}</td>
                                            <td className="px-4 py-3 text-right font-mono">{fmt(row.gross_amount)}</td>
                                            <td className="px-4 py-3 text-right font-mono">{fmt(row.net_payable)}</td>
                                            <td className="px-4 py-3 text-center">
                                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                                                    {row.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => openVoucherSource(row)}
                                                    data-testid={`ap-wtax-open-source-${row.voucher_number}`}
                                                    aria-label={`Open AP Voucher ${row.voucher_number || ''} source evidence`}
                                                    title={`Open AP Voucher ${row.voucher_number || ''} source evidence`}
                                                >
                                                    <FileText className="w-4 h-4 mr-1" /> Open Source
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
